import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Spinner from '../components/common/Spinner';
import { generateFatLossReportHTML } from '../services/fatLossReportService';
import { requestPDF } from '../services/pdfService';
import { toast } from 'react-toastify';
import { fatLossReportService } from '../services/fatLossReportService';
import { generatePDFFilename } from '../utils/generatePDFFilename';
import { patientService } from '../services/patientService';

const FatLossReportPreviewPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [patientName, setPatientName] = useState('');
  const [orderId, setOrderId] = useState('');

  useEffect(() => {
    const createPreview = async () => {
      try {
        let reportData = null;

        // First try sessionStorage (from form preview)
        const storedData = sessionStorage.getItem('fatLossReportData');
        if (storedData) {
          reportData = JSON.parse(storedData);
          sessionStorage.removeItem('fatLossReportData');
        } else {
          // If no sessionStorage, try to fetch from API
          try {
            const response = await fatLossReportService.getReportByAppointmentId(patientId);
            if (response && response.data) {
              reportData = response.data;
            }
          } catch (apiError) {
            console.log('Could not fetch from API:', apiError);
          }
        }

        if (!reportData) {
          throw new Error('No report data provided');
        }

        // Fetch patient data separately for filename (don't modify reportData)
        const { data: patientResp } = await patientService.getPatientById(patientId);
        const patient = patientResp?.data;

        const fetchedPatientName = patient?.profile?.name;
        const fetchedOrderId = patient?.sukra_order_id || patient?.id;

        // Store patient info for download
        setPatientName(fetchedPatientName || '');
        setOrderId(fetchedOrderId || '');

        console.log('📄 Preview Page - Patient Info:');
        console.log('Patient Name:', fetchedPatientName);
        console.log('Order ID:', fetchedOrderId);

        const html = await generateFatLossReportHTML(reportData);
        setHtmlContent(html);
      } catch (error) {
        console.error('Failed to generate preview', error);
        toast.error('Failed to generate preview');
        setHtmlContent('<div style="padding: 40px; text-align: center; color: #dc2626;"><p>Failed to generate preview</p></div>');
      } finally {
        setLoading(false);
      }
    };

    createPreview();
  }, [patientId]);

  const handleDownload = async () => {
    if (!htmlContent) return;
    try {
      setDownloading(true);
      const blob = await requestPDF(htmlContent);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const finalPatientName = patientName || 'patient';
      const finalOrderId = orderId || patientId;
      link.download = generatePDFFilename(finalPatientName, 'fatloss', finalOrderId);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-full px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-bold text-gray-800">Fat Loss Report Preview</h1>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/fatloss-report/${patientId}`)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                Back to Form
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-orange-300 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                {downloading ? (
                  <>
                    <Spinner size="sm" />
                    Downloading...
                  </>
                ) : (
                  'Download PDF'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content - A4 sized container for proper PDF rendering */}
      <div className="flex justify-center py-8 px-4">
        <div style={{
          width: '794px',
          backgroundColor: '#f5f5f5',
          padding: '0px'
        }}>
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </div>
      </div>
    </div>
  );
};

export default FatLossReportPreviewPage;
